import { ConsoleLog } from './consoleLog';
import { ShiftChecker } from './ShiftChecker';
import { allModulesRegexStr } from './const/authModules';

const logger = new ConsoleLog();
const Joi = require('joi');
const moment = require('moment-timezone');
const uuid = require('uuid');


export function validateDateString(dateString) {
  try {
    const testDate = moment(dateString, ["MM-DD-YYYY", "MM/DD/YYYY"], true);
    return testDate.isValid();
  } catch (e) {
    return false;
  }
}

const guidOrAlphanumeric = /^(?:[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}|[0-9a-zA-Z]+)$/;
const guidIdentifier = /^(?:[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})$/;

const err = (prefix, name) => ({ message: `validation.${prefix}.${name}` });

const rateSchema = (prefix = 'common') => ({
  // if a runRate is specified in the object, that's the min, otherwise 1 (api needs to validate bestRate)
  bestRate: Joi.number()
    .min(Joi.ref('runRate', { default: 1 }))
    .default(3600) // matches client defaults, allows creating minimum products for Order Execution
    .error(() => err(prefix, 'bestRate')),
  runRate: Joi.number()
    .min(1)
    .default(3000) // matches client defaults, allows creating minimum products for Order Execution
    .error(() => err(prefix, 'runRate')),
  multiplier: Joi.number()
    .default(1) // matches client defaults, allows creating minimum products for Order Execution
    .min(0.00001)
    .error(() => err(prefix, 'multiplier')),
});

const productLineRate = Joi.object().keys({
  lineId: Joi.string().required().guid().error(() => err('productLineRate', 'lineId')),
  ...rateSchema(),
});

const regexAlphaNumeric = /^([A-Za-z0-9]+)$/;
const regexModules = new RegExp(allModulesRegexStr, 'i');
const stringArray = Joi.array().items(Joi.string());
const moduleInfo = Joi.object({ stringArray }).keys().pattern(regexModules, Joi.string()).required();
const siteRoles = Joi.object({ moduleInfo }).keys().pattern(regexAlphaNumeric, Joi.object());

const basicUserInfo = {
  email: Joi.string()
    .email()
    .lowercase() // convert lowercase
    .required()
    .error(() => err('user', 'email')),
  siteRoles,
};

const passwords = {
  // https://stackoverflow.com/a/19605207
  password: Joi.string().regex(/^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9]).{8,}$/).required().error(() => err('user', 'password')),
  confirmPassword: Joi.any().equal(Joi.ref('password')).required().error(() => err('user', 'confirmPassword')),
};

const inspectionItem = Joi.object().keys({
  failureInstructionsFilename: Joi.string(),
  id: Joi.string().guid().default(() => uuid.v4(), 'uuid v4'),
  title: Joi.string().required().error(() => err('inspectionItem', 'title')),
  type: Joi.number().required(),
  required: Joi.boolean().required(),
  instructions: Joi.string(),
  maxValue: Joi.number().min(Joi.ref('minValue')),
  minValue: Joi.number(),
  referenceValue: Joi.number(),
  referenceOperator: Joi.number(),
  referenceImageFilename: Joi.string(),
});

const assessmentActivity = Joi.object().keys({
  comment: Joi.string(),
  failureInstructionsFilename: Joi.string(),
  id: Joi.string().regex(guidIdentifier, 'guid').required().error(() => err('assessmentActivity', 'id')),
  imageFilename: Joi.string(),
  instructions: Joi.string(),
  maxValue: Joi.number().min(Joi.ref('minValue')),
  minValue: Joi.number(),
  passed: Joi.boolean(),
  referenceImageFilename: Joi.string(),
  referenceOperator: Joi.number(),
  referenceValue: Joi.number(),
  required: Joi.boolean().required(),
  title: Joi.string(),
  type: Joi.number().required(),
  value: Joi.string(),
});

/**
 *
 * @param {[object]} validShiftItems - items from the schedules table
 * @param {scheduleTypes} scheduleType - type to check for overlap
 * @param {object} shiftItemToValidate - item to check overlap with
 * @returns {bool} returns true if there is no overlapping
 */
export function checkScheduleOverlapByType(validShiftItems, scheduleType, shiftItemToValidate) {
  logger.trace('%j', { fn: 'checkScheduleOverlapByType', message: 'start', validShiftItems, scheduleType, shiftItemToValidate });
  const events = getEventsByLineIds(validShiftItems, scheduleType);
  // return (events!==undefined);

  const checker = new ShiftChecker(validShiftItems);
  logger.debug('%j', { fn: 'checkScheduleOverlapByType', checker });
  let overlappingShifts = checker.shiftsDuring(shiftItemToValidate.startTime, shiftItemToValidate.endTime);
  overlappingShifts = overlappingShifts.filter(e => e.instanceInfo && e.lineIds && e.lineIds.some(i => shiftItemToValidate.lineIds.includes(i)));

  return !(overlappingShifts.length > 0);
}

/**
 *
 * @param {[object]} events
 * @param {number} scheduleTypeId - type to check for overlap
 * @param LineIds
 * @returns {*}
 */
export const getEventsByLineIds = (events, scheduleTypeId, LineIds = []) => {
  if (!events) return undefined;
  let returnArray = [];
  if (!LineIds || LineIds.length < 1) {
    returnArray = events.filter(e => e.typeId === scheduleTypeId);
  } else {
    returnArray = events.filter(e => e.typeId === scheduleTypeId && e.lineIds.some(l => LineIds.indexOf(l) >= -1));
    // returnArray = events.filter(e => e.typeId === scheduleType.value && e.lineIds.includes(LineIds));
  }
  return returnArray;
};


export const scheduleTypes = {
  shift: { value: 1, color: '#56ca85' },
  maintenance: { value: 2, color: '#f8a398' },
  break: { value: 3, color: '#cac33b' },
  autoStandby: { value: 4, color: '#bfbfbf' },

};

export const reportTypes = ['compliance', 'downtime', 'oee', 'product', 'shift'];

export const dashConfigLimits = {
    default: 20,
    min: 5,
    max: 60
  };

export const downtimeThresholdLimits = {
  default: 5,
  min: 1,
  max: 10
};

const apiKeyJoiSchema = Joi.object().keys({
  keyId: Joi.string().required(),
  keyValue: Joi.string().required()
});

export const schemas = {
  // Downtime Reason
  downtimeReason: Joi.object().keys({
    createdAt: Joi.string().optional(),
    createdBy: Joi.string().optional(),
    description: Joi.string().optional(),
    display: Joi.object({
      color: Joi.string(),
    }).optional(),
    icon: Joi.string(),
    id: Joi.string().guid().default(() => uuid.v4(), 'uuid v4'),
    reasonNote: Joi.string().max(255).optional(),
    subReasonNote: Joi.string().max(255).optional(),
    parentId: Joi.string().guid().optional(),
    childIds: Joi.array().items(Joi.string().guid()).optional(),
    isDeleted: Joi.boolean().default(false),
    recoveryMin: Joi.number().default(10),
    siteId: Joi.string().regex(guidOrAlphanumeric, 'alpha numeric or guid').required().error(() => err('downtimeReason', 'siteId')),
    title: Joi.string().required().error(() => err('downtimeReason', 'title')),
    typeId: Joi.string().valid('1', '2', '3').default('3'),  // 1 planned, 2 unplanned, 3 unassigned
    isSubcategory: Joi.boolean().default(false),
    updatedBy: Joi.string(),
  }),
  // Inspection
  inspection: Joi.object().keys({
    active: Joi.boolean().default(false),
    createdBy: Joi.string(),
    description: Joi.string().optional().error(() => err('inspection', 'description')),
    endAfterDate: Joi.date().optional().strip(),
    endTime: Joi.date().optional().strip(),
    freqType: Joi.string().required(),
    id: Joi.string().guid().default(() => uuid.v4(), 'uuid v4'),
    inspectionItems: Joi.array().items(inspectionItem),
    isDeleted: Joi.boolean().default(false),
    lineIds: Joi.array().items(Joi.string().guid()).meta({ dynamoType: 'SS' }),
    lineId: Joi.string().guid().optional(),
    minutesBetween: Joi.number().min(5),
    minutesDue: Joi.number().min(5),
    productIds: Joi.array().items(Joi.string().guid()).meta({ dynamoType: 'SS' }),
    recurrenceRule: Joi.string(),
    repeatType: Joi.string(),
    roleId: Joi.string().guid(),
    shiftId: Joi.string(),
    siteId: Joi.string().regex(guidOrAlphanumeric, 'alpha numeric or guid').required(),
    startTime: Joi.date().raw().required(),
    title: Joi.string().required().error(() => err('inspection', 'title')),
    updatedBy: Joi.string(),

  }),
  // Assessment
  assessment: Joi.object().keys({
    activities: Joi.array().items(assessmentActivity),
    approvedBy: Joi.string().guid(),
    approvedDt: Joi.string(),
    createdBy: Joi.string().required(),
    createdBySystem: Joi.boolean(),
    description: Joi.string(),
    debug: Joi.any(),
    dueDate: Joi.string(),
    expiry: Joi.number(),
    id: Joi.string().required(),
    inspectionId: Joi.string().regex(guidIdentifier, 'guid').required().error(() => err('assessment', 'inspectionId')),
    minutesDue: Joi.number().optional(),
    productId: Joi.string().guid(),
    overallStatus: Joi.string(),
    roleId: Joi.string().regex(guidOrAlphanumeric, 'alpha numeric or guid'),
    shiftId: Joi.string().guid(),
    signedAt: Joi.string(),
    signedBy: Joi.string(),
    signedDt: Joi.string(),
    siteId: Joi.string().regex(guidOrAlphanumeric, 'alpha numeric or guid').required().error(() => err('inspection', 'siteId')),
    skippedBy: Joi.string().guid(),
    skippedDt: Joi.string(),
    skipReason: Joi.string().optional(),
    startDt: Joi.string(),
    startLine: Joi.string().optional(),
    status: Joi.string().required(),
    title: Joi.string().required(),
    updatedBy: Joi.string(),
  }),
  // Line
  line: Joi.object().keys({
    assignedProductId: Joi.string(),
    assignedProductRun: Joi.object({
      targetCount: Joi.number(),
      startDt: Joi.string(),
      endDt: Joi.string(),
    }).optional(),
    automaticPlannedDowntimeStart: Joi.boolean().default(false),
    ...rateSchema(),
    createdBy: Joi.string(),
    description: Joi.string().optional(),
    deviceAssignments: Joi.array().items({
      roleId: Joi.string(),
      deviceId: Joi.string(),
    }).optional(),
    display: Joi.object({
      color: Joi.string(),
    }).optional(),
    displayDetails: Joi.any(),
    groupName: Joi.string().default('Default Group'),
    // default to a guid
    id: Joi.string().guid().default(() => uuid.v4(), 'uuid v4'),
    isDeleted: Joi.boolean().default(false),

    printerId: Joi.string().error(() => err('line', 'printerId')),
    printerRoleId: Joi.string().valid('1', '2', '3').default('1', 'each'), // each, case, pallet
    quantity: Joi.number().default(1),
    // we could dis-allow guid based sites (if we wanted to further restrict personal sites)
    siteId: Joi.string().regex(guidOrAlphanumeric, 'alpha numeric or guid').required().error(() => err('line', 'siteId')),
    title: Joi.string().required().error(() => err('line', 'title')),
    updatedBy: Joi.string(),
  }),
  // Product
  product: Joi.object().keys({
    ...rateSchema(),
    createdBy: Joi.string(),
    description: Joi.string().optional(),
    id: Joi.string().guid().default(() => uuid.v4(), 'uuid v4'),
    imageBase64: Joi.string(),
    imageUrl: Joi.string(),
    isDeleted: Joi.boolean().default(false),
    lineIds: Joi.array().items(productLineRate),
    allowedLineIds: Joi.array().optional(),
    display: Joi.object({
      color: Joi.string(),
    }).optional(),
    printerRoleId: Joi.string().optional().valid('1', '2', '3'),
    productIdentifier: Joi.string().optional().allow(null),
    siteId: Joi.string().regex(guidOrAlphanumeric, 'alpha numeric or guid').required().error(() => err('product', 'siteId')),
    // api enforces unique to site
    title: Joi.string().required().error(() => err('product', 'title')),
    updatedBy: Joi.string(),
  }),
  // ?
  productLineRate,
  // Shift
  schedule: Joi.object().keys({
    companyId: Joi.string().regex(guidOrAlphanumeric, 'alpha numeric or guid').required(), // .error(() => err('schedule', 'companyId')),
    createdAt: Joi.string().optional(),
    createdBy: Joi.string().optional(),
    description: Joi.string().optional(),
    endTime: Joi.date().greater(Joi.ref('startTime')).raw(), // .error(() => err('schedule', 'endTime')),
    id: Joi.string().guid().default(() => uuid.v4(), 'uuid v4'),
    isAllDay: Joi.boolean().default(false),
    isDeleted: Joi.boolean().default(false),
    siteId: Joi.string().regex(guidOrAlphanumeric, 'alpha numeric or guid').required(), // .error(() => err('schedule', 'siteId')),
    lineIds: Joi.array(),
    recurrenceRule: Joi.any(),
    recurrenceException: Joi.any(),
    startTime: Joi.date().raw(),
    title: Joi.string().required(), // .error(() => err('schedule', 'title')),
    typeId: Joi.number().valid(1, 2, 3, 4).default(1), // shift, planned-Downtime ,break, auto-assign
    updatedAt: Joi.date().raw(),
    updatedBy: Joi.any().optional(),
    tz: Joi.any().strip(),
    /*  not needed yet  updatedAt: Joi.date().raw(),*/
  }),
  // Site
  site: Joi.object().keys({
    id: Joi.string().regex(guidOrAlphanumeric, 'alpha numeric or guid').required().error(() => err('site', 'id')),
    address: Joi.string().optional(),
    authModulesVersion: Joi.number().optional(),
    bestRate: Joi.any().strip(),
    companyId: Joi.string().regex(guidOrAlphanumeric, 'alpha numeric or guid').required().error(() => err('site', 'companyId')),
    title: Joi.string().required().error(() => err('site', 'title')),
    description: Joi.string().optional(),
    contactEmail: Joi.string().email().required().error(() => err('site', 'contactEmail')),
    notificationsDelay: Joi.number().default(10).min(5).error(() => err('site', 'notificationsDelay')),
    notificationsEnabled: Joi.boolean().default(false),
    isDeleted: Joi.boolean().default(false),
    createdAt: Joi.string().raw(),
    createdBy: Joi.string(),
    updatedAt: Joi.date().raw(),
    updatedBy: Joi.string(),
    trialStart: Joi.date().raw(),
    modules: Joi.array().items(Joi.string()).meta({ dynamoType: 'SS' }),
    slowCycleThreshold: Joi.number().default(1),
    smallStopCategory: Joi.string().valid('availability', 'performance').default('availability'),
    subscriptionStatus: Joi.string().default('trial'),
    tz: Joi.string().valid(moment.tz.names()).required().error(() => err('site', 'tz')),
    siteAdmins: Joi.array().items(Joi.string()).meta({ dynamoType: 'SS' }),
    resName: Joi.string().optional(),
    usagePlanType: Joi.string().optional().valid('lowUsage', 'defaultUsage', 'highUsage'),
    activeApiKeys: Joi.array().items(apiKeyJoiSchema).optional(),
    apiKeys: Joi.array().items(apiKeyJoiSchema).optional(),
    roleNames: Joi.array().items(Joi.string()).meta({ dynamoType: 'SS' }),
    runRate: Joi.any().strip(),
    complianceSettings: Joi.object().keys({
      assessmentExpirationMonths: Joi.number().default(60).min(12).max(180)
        .error(() => err('complianceSettings', 'assessmentExpirationMonths')),
    }),
    dashConfig: Joi.object()
      .keys({
        cycleLines: Joi.boolean().default(true),
        delay: Joi.when('cycleLines', { is: true, then: Joi.number().default(dashConfigLimits.default).min(dashConfigLimits.min).max(dashConfigLimits.max)}),
      }).optional(),
    downtimeThreshold: Joi.number().default(downtimeThresholdLimits.default).min(downtimeThresholdLimits.min).max(downtimeThresholdLimits.max).optional(),
  }),
  // User

  basicUserInfo: Joi.object().keys({ ...basicUserInfo }),
  siteRoles,
  login: Joi.object().keys({ ...basicUserInfo, ...passwords }),
  user: Joi.object().keys({
    ...basicUserInfo,
    ...passwords,
    firstName: Joi.string().required().error(() => err('user', 'firstName')),
    lastName: Joi.string().required().error(() => err('user', 'lastName')),
    company: Joi.string().required().error(() => err('user', 'company')),
    country: Joi.string().required().error(() => err('user', 'country')),
  }),
  // user defined report
  reportUserNotification: Joi.object().keys({
    userId: Joi.string().required().guid().error(() => err('reportUserNotification', 'userId')),
    distributeTo: Joi.array().items(Joi.string().guid().error(() => err('reportUserNotification', 'distributeTo'))),
    title: Joi.string().required().error(() => err('reportUserNotification', 'title')),
    siteId: Joi.string().required().error(() => err('reportUserNotification', 'siteId')),
    createdBy: Joi.string(),
    updatedBy: Joi.string(),
    urlTemplate: Joi.string().required().error(() => err('reportUserNotification', 'urlTemplate')),
    timeFrame: Joi.string().required().error(() => err('reportUserNotification', 'timeFrame')),
    type: Joi.string().allow(reportTypes).default('compliance'),
  }),
  // site defined report
  reportSiteNotification: Joi.object().keys({
    siteId: Joi.string().required().error(() => err('reportSiteNotification', 'siteId')),
    title: Joi.string().required().error(() => err('reportSiteNotification', 'title')),
    createdBy: Joi.string(),
    updatedBy: Joi.string(),
    urlTemplate: Joi.string().required().error(() => err('reportSiteNotification', 'urlTemplate')),
    timeFrame: Joi.string().required().error(() => err('reportSiteNotification', 'timeFrame')),
    type: Joi.string().allow(reportTypes).default('compliance'),
  }),
  // report schedule
  reportNotifyUserSchedule: Joi.object().keys({
    createdBy: Joi.string(),
    localReports: Joi.array().items(Joi.string()),
    schedule: {
      active: Joi.boolean(),
      freq: Joi.string().required().error(() => err('reportNotifyUserSchedule', 'freq')),
      options: Joi.any() },
    siteId: Joi.string().required().error(() => err('reportNotifyUserSchedule', 'siteId')),
    siteReports: Joi.array().items(Joi.string()),
    title: Joi.string().required().error(() => err('reportNotifyUserSchedule', 'title')),
    updatedBy: Joi.string(),
    distributedBy: Joi.string().guid().error(() => err('reportNotifyUserSchedule', 'distributedBy')),
    distributeTo: Joi.array().items(Joi.string().guid().error(() => err('reportNotifyUserSchedule', 'distributeTo'))),
    userId: Joi.string().required().guid().error(() => err('reportNotifyUserSchedule', 'userId')),
  }),
  odexConfig: Joi.object().keys({
    id: Joi.string().guid().default(() => uuid.v4(), 'uuid v4'),
    printerRoleId: Joi.string().guid().required().error(() => err('odexConfig', 'roleId')),
    template: Joi.string().required().error(() => err('odexConfig', 'template')),
    siteId: Joi.string().required().error(() => err('odexConfig', 'siteId')),
    roleTemplate: Joi.string(),
    productIds: Joi.array().items(Joi.string().guid()).meta({ dynamoType: 'SS' }),
    createdBy: Joi.string(),
    updatedBy: Joi.string(),
  }),
  deviceRole: Joi.object().keys({
    id: Joi.string().guid().required().error(() => err('deviceRole', 'id')),
    siteId: Joi.string().required().error(() => err('deviceRole', 'siteId')),
    title: Joi.string().required().error(() => err('deviceRole', 'title')),
    siteDeviceRoleId: Joi.string().optional(),
    createdBy: Joi.string(),
    updatedBy: Joi.string(),
  }),
};
